import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Users,
  Search,
  Filter,
  Plus,
  Upload,
  ChevronDown,
  MoreHorizontal,
  Phone,
  Mail,
  RefreshCw,
  ArrowUpDown,
  X,
  Settings2,
  Tag,
  Calendar,
  Clock,
  Trash2,
  Edit,
  Eye,
  Building2,
  Zap,
  Download,
  MoreVertical,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Checkbox } from '../../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import { formatDateTimeWithTZ } from '../../../utils/timezone';

// Import shared components
import {
  BulkActionBar,
  AddToAutomationModal,
  AdvancedFiltersPanel,
  SmartListTabs,
} from '../../../components/contacts/ContactsShared';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Platform Contacts Page - GHL-Style CRM Layout
 * 
 * Features:
 * - Full-width table layout (no side column)
 * - Smart Lists as horizontal tabs
 * - Bulk action bar when contacts selected
 * - Add to Automation modal
 * - Advanced Filters right-side panel
 */
const PlatformContactsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Smart lists
  const [smartLists, setSmartLists] = useState([]);
  const [selectedSmartList, setSelectedSmartList] = useState(null);
  
  // Filter metadata
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  
  // Agencies for filter dropdown
  const [agencies, setAgencies] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, leads: 0, clients: 0 });
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    phone: true,
    email: true,
    agency: true,
    status: true,
    contact_type: true,
    created_at: true,
    last_activity_at: true,
    tags: true,
  });
  
  // Selection
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modals
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Main tabs
  const [activeTab, setActiveTab] = useState('contacts');
  const [bulkJobs, setBulkJobs] = useState([]);
  const [bulkJobsLoading, setBulkJobsLoading] = useState(false);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedAgency) params.append('agency_id', selectedAgency);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedType) params.append('contact_type', selectedType);
      if (selectedSmartList) params.append('smart_list_id', selectedSmartList);
      if (advancedFilters.length > 0) {
        params.append('filters', JSON.stringify(advancedFilters));
      }
      
      const res = await axios.get(`${API}/platform/contacts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContacts(res.data.contacts || []);
      setTotalContacts(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [token, page, perPage, searchQuery, selectedAgency, selectedStatus, selectedType, sortBy, sortOrder, selectedSmartList, advancedFilters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const params = selectedAgency ? `?agency_id=${selectedAgency}` : '';
      const res = await axios.get(`${API}/platform/contacts/stats${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token, selectedAgency]);

  // Fetch smart lists
  const fetchSmartLists = useCallback(async () => {
    try {
      const params = selectedAgency ? `?agency_id=${selectedAgency}` : '';
      const res = await axios.get(`${API}/platform/contacts/smart-lists/all${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartLists(res.data.smart_lists || []);
    } catch (err) {
      console.error('Failed to fetch smart lists:', err);
    }
  }, [token, selectedAgency]);

  // Fetch agencies
  const fetchAgencies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/contacts/filters/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(res.data.options || []);
    } catch (err) {
      console.error('Failed to fetch agencies:', err);
    }
  }, [token]);
  
  // Fetch bulk action jobs
  const fetchBulkJobs = useCallback(async () => {
    setBulkJobsLoading(true);
    try {
      const res = await axios.get(`${API}/platform/contacts/bulk-actions/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulkJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch bulk jobs:', err);
      setBulkJobs([]);
    } finally {
      setBulkJobsLoading(false);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    fetchContacts();
    fetchStats();
    fetchSmartLists();
    fetchAgencies();
  }, [fetchContacts, fetchStats, fetchSmartLists, fetchAgencies]);
  
  // Fetch bulk jobs when tab changes
  useEffect(() => {
    if (activeTab === 'bulk-actions') {
      fetchBulkJobs();
    }
  }, [activeTab, fetchBulkJobs]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (page === 1) {
        fetchContacts();
      } else {
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedContacts(contacts.map(c => c));
    } else {
      setSelectedContacts([]);
    }
  };

  // Handle select all matching (total)
  const handleSelectAllMatching = () => {
    // In production, this would fetch all matching IDs
    toast.info(`Selected all ${totalContacts} contacts`);
  };

  // Handle individual select
  const handleSelectContact = (contact, checked) => {
    if (checked) {
      setSelectedContacts([...selectedContacts, contact]);
    } else {
      setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
      setSelectAll(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
    });
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'inactive': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'do_not_contact': return 'bg-red-100 text-red-700 border-red-200';
      case 'archived': return 'bg-slate-100 text-slate-500 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // Get type badge color
  const getTypeColor = (type) => {
    switch (type) {
      case 'lead': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'prospect': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'client': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'former_client': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'self_repair': return 'bg-teal-100 text-teal-700 border-teal-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // Bulk action handlers
  const handleBulkExport = () => {
    toast.info('Exporting contacts...');
  };

  const handleBulkSendEmail = () => {
    toast.info('Opening email composer...');
  };

  const handleBulkAddTags = () => {
    toast.info('Opening tag manager...');
  };

  const handleBulkDelete = () => {
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };
  
  const handleConfirmDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      // Extract just the IDs from the contact objects
      const contactIds = selectedContacts.map(c => c.id || c);
      
      const res = await axios.post(
        `${API}/platform/contacts/bulk-delete`,
        { contact_ids: contactIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Successfully deleted ${res.data.deleted_count || selectedContacts.length} contacts`);
      setShowDeleteModal(false);
      setSelectedContacts([]);
      setSelectAll(false);
      setDeleteConfirmText('');
      fetchContacts();
      fetchStats();
      fetchBulkJobs(); // Refresh bulk jobs to show the new delete operation
    } catch (err) {
      console.error('Failed to delete contacts:', err);
      // Handle different error formats from backend
      let errorMessage = 'Failed to delete contacts';
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          // Pydantic validation error array
          errorMessage = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof data.detail === 'object') {
          errorMessage = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
        } else if (data.message) {
          errorMessage = data.message;
        }
      }
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkMore = (actionId) => {
    if (actionId === 'trigger_automation') {
      setShowAutomationModal(true);
    } else {
      toast.info(`Action: ${actionId}`);
    }
  };

  return (
    <div className="min-w-0 max-w-full flex flex-col min-h-full bg-slate-50" data-testid="platform-contacts-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">Contacts</h1>
              <Badge variant="secondary" className="text-sm bg-blue-100 text-blue-700">
                {totalContacts.toLocaleString()} Contacts
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                data-testid="import-contacts-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="add-contact-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Download className="w-4 h-4 mr-2" />
                    Export All
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings2 className="w-4 h-4 mr-2" />
                    Manage Fields
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Main Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-6 border-b border-slate-200">
            <TabsList className="bg-transparent h-auto p-0 gap-6">
              <TabsTrigger 
                value="contacts" 
                className="px-0 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent"
              >
                Contacts
              </TabsTrigger>
              <TabsTrigger 
                value="smart-lists" 
                className="px-0 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent text-blue-600"
              >
                Smart Lists
              </TabsTrigger>
              <TabsTrigger 
                value="bulk-actions" 
                className="px-0 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent"
              >
                Bulk Actions
              </TabsTrigger>
              <TabsTrigger 
                value="tasks" 
                className="px-0 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent"
              >
                Tasks
              </TabsTrigger>
              <TabsTrigger 
                value="companies" 
                className="px-0 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent"
              >
                Companies
              </TabsTrigger>
            </TabsList>
          </div>
        
          {/* Contacts Tab Content */}
          <TabsContent value="contacts" className="m-0">
            {/* Smart List Sub-Tabs */}
            <div className="bg-white border-b border-slate-200">
              <SmartListTabs
                smartLists={smartLists}
                selectedList={selectedSmartList}
                onSelectList={setSelectedSmartList}
                onAddList={() => toast.info('Add smart list')}
                totalContacts={stats.total}
              />
            </div>

      {/* Bulk Action Bar */}
      {selectedContacts.length > 0 && (
        <div className="px-6 py-3 bg-white border-b border-slate-200">
          <BulkActionBar
            selectedCount={selectedContacts.length}
            totalCount={totalContacts}
            onSelectAll={handleSelectAllMatching}
            onDeselectAll={() => { setSelectedContacts([]); setSelectAll(false); }}
            onExport={handleBulkExport}
            onTriggerAutomation={() => setShowAutomationModal(true)}
            onSendEmail={handleBulkSendEmail}
            onAddTags={handleBulkAddTags}
            onDelete={handleBulkDelete}
            onMore={handleBulkMore}
            scope="platform"
          />
        </div>
      )}

      {/* Main Content - Full Width */}
      <div className="p-6 min-w-0">
        {/* Toolbar */}
        <div className="flex items-center gap-4 mb-4 bg-white rounded-lg border border-slate-200 p-4">
          {/* Agency Filter */}
          <Select value={selectedAgency || 'all'} onValueChange={(v) => setSelectedAgency(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[200px]" data-testid="agency-filter">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Agencies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agencies</SelectItem>
              {agencies.map((agency) => (
                <SelectItem key={agency.value} value={agency.value}>
                  {agency.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-contacts"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Quick Filters */}
          <Select value={selectedStatus || 'all'} onValueChange={(v) => setSelectedStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedType || 'all'} onValueChange={(v) => setSelectedType(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="former_client">Former Client</SelectItem>
              <SelectItem value="self_repair">Self Repair</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced Filters Button */}
          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(true)}
            className={cn(
              advancedFilters.length > 0 && 'bg-blue-50 border-blue-200 text-blue-700'
            )}
            data-testid="advanced-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Advanced Filters
            {advancedFilters.length > 0 && (
              <Badge className="ml-2 bg-blue-600 text-white text-xs">
                {advancedFilters.length}
              </Badge>
            )}
          </Button>

          {/* Manage Fields */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="manage-fields-btn">
                <Settings2 className="w-4 h-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {Object.entries(visibleColumns).map(([key, visible]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => setVisibleColumns({ ...visibleColumns, [key]: !visible })}
                >
                  <Checkbox checked={visible} className="mr-2" />
                  {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Refresh */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { fetchContacts(); fetchStats(); }}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
        </div>

        {/* Contacts Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left w-12">
                    <Checkbox 
                      checked={selectAll && contacts.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  {visibleColumns.name && (
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                      onClick={() => handleSort('last_name')}
                    >
                      <div className="flex items-center gap-1">
                        Contact Name
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  )}
                  {visibleColumns.phone && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Phone
                    </th>
                  )}
                  {visibleColumns.email && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Email
                    </th>
                  )}
                  {visibleColumns.agency && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Agency
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {visibleColumns.contact_type && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Type
                    </th>
                  )}
                  {visibleColumns.created_at && (
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  )}
                  {visibleColumns.last_activity_at && (
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                      onClick={() => handleSort('last_activity_at')}
                    >
                      <div className="flex items-center gap-1">
                        Last Activity
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  )}
                  {visibleColumns.tags && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Tags
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-12 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
                      <p className="text-slate-500 mt-2">Loading contacts...</p>
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-12 text-center">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-700 font-medium">No contacts found</p>
                      <p className="text-slate-500 text-sm mt-1">Try adjusting your filters or add a new contact</p>
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact) => (
                    <tr 
                      key={contact.id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedContacts.some(c => c.id === contact.id)}
                          onCheckedChange={(checked) => handleSelectContact(contact, checked)}
                        />
                      </td>
                      {visibleColumns.name && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                              {contact.first_name?.[0]}{contact.last_name?.[0]}
                            </div>
                            <div>
                              <button
                                onClick={() => navigate(`/platform/contacts/${contact.id}`)}
                                className="text-slate-900 font-medium hover:text-blue-600 hover:underline text-left transition-colors"
                                data-testid={`contact-name-${contact.id}`}
                              >
                                {contact.full_name || `${contact.first_name} ${contact.last_name}`}
                              </button>
                              {contact.company_name && (
                                <p className="text-xs text-slate-500">{contact.company_name}</p>
                              )}
                            </div>
                          </div>
                        </td>
                      )}
                      {visibleColumns.phone && (
                        <td className="px-4 py-3">
                          {contact.phone ? (
                            <div className="flex items-center gap-1.5 text-slate-700">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              {contact.phone}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.email && (
                        <td className="px-4 py-3">
                          {contact.email ? (
                            <div className="flex items-center gap-1.5 text-slate-700">
                              <Mail className="w-3.5 h-3.5 text-slate-400" />
                              <span className="truncate max-w-[200px]">{contact.email}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.agency && (
                        <td className="px-4 py-3">
                          <span className="text-slate-700 text-sm">{contact.agency_name || '-'}</span>
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="px-4 py-3">
                          <Badge className={cn('text-xs border', getStatusColor(contact.status))}>
                            {contact.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                      )}
                      {visibleColumns.contact_type && (
                        <td className="px-4 py-3">
                          <Badge className={cn('text-xs border', getTypeColor(contact.contact_type))}>
                            {contact.contact_type?.replace('_', ' ')}
                          </Badge>
                        </td>
                      )}
                      {visibleColumns.created_at && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(contact.created_at)}
                          </div>
                        </td>
                      )}
                      {visibleColumns.last_activity_at && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                            <Clock className="w-3.5 h-3.5" />
                            {formatRelativeTime(contact.last_activity_at)}
                          </div>
                        </td>
                      )}
                      {visibleColumns.tags && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap max-w-[150px]">
                            {contact.tags?.slice(0, 2).map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs text-blue-600 border-blue-200 bg-blue-50">
                                {tag}
                              </Badge>
                            ))}
                            {contact.tags?.length > 2 && (
                              <Badge variant="outline" className="text-xs text-slate-500">
                                +{contact.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/platform/contacts/${contact.id}`)}>
                              <Eye className="w-4 h-4 mr-2" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Showing {contacts.length} of {totalContacts} contacts
              </span>
              <Select value={perPage.toString()} onValueChange={(v) => { setPerPage(parseInt(v)); setPage(1); }}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters Side Panel */}
      <AdvancedFiltersPanel
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        onApply={(filters) => {
          setAdvancedFilters(filters);
          setShowAdvancedFilters(false);
          fetchContacts();
        }}
        onClear={() => {
          setAdvancedFilters([]);
          fetchContacts();
        }}
      />
          </TabsContent>
          
          {/* Smart Lists Tab Content */}
          <TabsContent value="smart-lists" className="m-0 p-6">
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <h3 className="text-lg font-medium text-slate-900 mb-2">Smart Lists</h3>
              <p className="text-slate-600">
                Create and manage smart lists to organize your contacts.
              </p>
            </div>
          </TabsContent>
          
          {/* Bulk Actions Tab Content */}
          <TabsContent value="bulk-actions" className="m-0 p-6">
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Bulk actions</h3>
                <p className="text-sm text-slate-600 mt-1">Track progress and results for bulk actions.</p>
              </div>
              
              {/* Filters */}
              <div className="p-4 border-b border-slate-200 flex items-center gap-4 flex-wrap">
                <span className="text-sm text-slate-600">Filters</span>
                <Input type="date" className="w-auto" defaultValue="2025-01-11" />
                <Input type="date" className="w-auto" defaultValue="2026-05-02" />
                <Select defaultValue="all">
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                <Select defaultValue="all">
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="add_tags">Add Tags</SelectItem>
                    <SelectItem value="export">Export</SelectItem>
                  </SelectContent>
                </Select>
                <Select defaultValue="all">
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Action label</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Operation</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">User</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Created (EDT)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Completed (EDT)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Statistics</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkJobsLoading ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                          Loading bulk actions...
                        </td>
                      </tr>
                    ) : bulkJobs.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                          No bulk actions yet
                        </td>
                      </tr>
                    ) : (
                      bulkJobs.map((job) => (
                        <tr key={job.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-900">{job.label || job.id}</td>
                          <td className="px-4 py-3 text-sm text-slate-900">{job.operation || 'Delete'}</td>
                          <td className="px-4 py-3">
                            <Badge 
                              variant={job.status === 'complete' ? 'default' : job.status === 'failed' ? 'destructive' : 'secondary'}
                              className={job.status === 'complete' ? 'bg-green-100 text-green-700' : ''}
                            >
                              {job.status === 'complete' ? 'Complete' : job.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {job.user ? (
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-600">
                                  {job.user.substring(0, 2).toUpperCase()}
                                </div>
                                {job.user}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {job.created_at ? formatDateTimeWithTZ(job.created_at) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {job.completed_at ? formatDateTimeWithTZ(job.completed_at) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            <span className="text-green-600">{job.success_count || 0}</span>
                            {job.error_count > 0 && (
                              <span className="text-red-600 ml-2">/ {job.error_count} errors</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => toast.info(`View details for ${job.id}`)}>
                                  View Details
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="p-4 flex items-center justify-between border-t border-slate-200">
                <span className="text-sm text-slate-600">Page 1</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled>Previous</Button>
                  <Button variant="outline" size="sm" disabled>Next</Button>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Tasks Tab Content */}
          <TabsContent value="tasks" className="m-0 p-6">
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <h3 className="text-lg font-medium text-slate-900 mb-2">Tasks</h3>
              <p className="text-slate-600">
                Manage tasks related to your contacts.
              </p>
            </div>
          </TabsContent>
          
          {/* Companies Tab Content */}
          <TabsContent value="companies" className="m-0 p-6">
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <h3 className="text-lg font-medium text-slate-900 mb-2">Companies</h3>
              <p className="text-slate-600">
                Manage companies and their associated contacts.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add to Automation Modal */}
      <AddToAutomationModal
        isOpen={showAutomationModal}
        onClose={() => setShowAutomationModal(false)}
        selectedContacts={selectedContacts}
        token={token}
        scope="platform"
      />
      
      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Bulk delete</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-lg font-medium mb-4">
              Delete {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''}?
            </p>
            
            <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mb-4">
              <span className="text-pink-600 font-medium">({selectedContacts.length})</span>
            </div>
            
            <div className="space-y-3 text-sm text-slate-600">
              <p>
                <span className="font-medium text-slate-900">Note :</span> Deleted contacts can be restored within 2 months
              </p>
              <p>
                Deleting any contacts will also remove the corresponding: Conversations, Notes, 
                Opportunities, Tasks, Appointments, Manual Actions, Community Group Owners. 
                It will also stop any active campaigns and workflows for the contacts.
              </p>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Type 'DELETE' to confirm
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full"
                data-testid="delete-confirm-input"
              />
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs">i</span>
                </div>
                <p className="text-sm text-blue-700">
                  Bulk actions are performed over a period of time. You can track the progress on the bulk actions page.
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmText('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformContactsPage;
